'use client'
import ExperienceGridCard, {
	ExperienceGridCardProps,
} from './ExperienceGridCard'
import {Button, SlideshowNative, SmallText} from '@local/ui/components'
import clsx from 'clsx'
import {useSearchParams, useRouter, usePathname} from 'next/navigation'
import {useMemo, useRef, useState} from 'react'
import {event} from '@local/utils/gtm'
type ExperienceType =
	| 'group-retreat'
	| 'private-collection'
	| 'kaer-for-business'
	| 'all'
	| 'destination'

function slugifyExperienceType (value: string | undefined): string {
	return value?.toLowerCase().replaceAll(' ', '-') ?? ''
}

/** URL + UI use private-collection; CMS may still store "Private Journey" → private-journey */
function tripTypeMatchesExperience (
	experienceType: string | undefined,
	tripFilter: string,
): boolean {
	const itemSlug = slugifyExperienceType(experienceType)
	const filterSlug = tripFilter.toLowerCase()
	if (itemSlug === filterSlug) return true
	return (
		(filterSlug === 'private-collection' && itemSlug === 'private-journey') ||
		(filterSlug === 'private-journey' && itemSlug === 'private-collection')
	)
}
interface ExperienceGridContentProps {
	title: string;
	surtitle: string;
	grid: ExperienceGridCardProps[];
	forHome?: boolean;
}

export const ExperienceGridContent = ({
	title,
	surtitle,
	grid,
	forHome = false,
}: ExperienceGridContentProps) => {
	const router = useRouter()
	const pathname = usePathname()
	const slideshowRef = useRef<HTMLDivElement>(null)
	const [selectedTab, setSelectedTab] = useState<ExperienceType | null>(null)
	const searchParams = useSearchParams()
	const destination = searchParams.get('destination')
	const tripType = searchParams.get('tripType')
	const guestCounts = searchParams.get('guestCounts')
	const fromHomeTab = searchParams.get('fromHomeTab')
	const isLeadershipPage = pathname.includes('/leadership')

	const onTabChange = (tab: ExperienceType) => {
		event('select_item', {
			ecommerce: {
				item_list_name: 'experience-type',
				items: [
					{
						item_id: tab,
						item_name: tab,
					},
				],
			},
		})
		router.push(`/experiences?tripType=${tab}&fromHomeTab=true`)
	}

	const filteredGrid = useMemo(() => {
		let result = grid

		if (destination) {
			result = grid.filter((item) => {
				if (tripType === 'kaer-for-business') {
					return true
				}
				if (!item?.location) return false

				return item?.location
					.toLowerCase()
					.replaceAll(' ', '-')
					.includes(destination.toLowerCase().replaceAll(' ', '-'))
			})
		}
		if (selectedTab) {
			return result.filter((item) =>
				tripTypeMatchesExperience(item.experienceType, selectedTab),
			)
		}
		if (tripType) {
			return result.filter((item) =>
				tripTypeMatchesExperience(item.experienceType, tripType),
			)
		}
		if (guestCounts) {
			return result.filter(
				(item) => item.maxPeopleCount || parseInt(guestCounts) <= 12,
			)
		}
		return result
	}, [grid, selectedTab, destination, tripType, guestCounts])

	return (
		<div className='px-10 flex flex-col items-center'>
			{forHome
				? (
					<div className=' flex flex-col items-center py-50 lg:py-100'>
						<SmallText>{title}</SmallText>
						<h3 className='font-serif font-medium text-28  lg:text-34 text-center text-black'>
							{surtitle}
						</h3>
						<div className='flex flex-row gap-8 mt-34'>
							<Button
								label='Group Retreats'
								className='text-9'
								variant='secondary-transparent'
								onClick={() => onTabChange('group-retreat')}
								small />
							<Button
								label='Private Collection'
								className='text-9'
								variant='secondary-transparent'
								onClick={() => onTabChange('private-collection')}
								small />
							<Button
								label='Kaer For Business'
								className='text-9'
								variant='secondary-transparent'
								onClick={() => router.push('/leadership/kaer-for-business')}
								small />
						</div>
					</div>
				)
				: (
					<div className='flex flex-col items-center py-50 lg:py-60'>
						{isLeadershipPage && (
							<h3 className='font-serif font-medium text-28  lg:text-34 text-center text-black'>
								{surtitle}
							</h3>
						)}
						{filteredGrid.length === 0 && (
							<div className='flex flex-col items-center'>
								<SmallText>Nothing listed here yet</SmallText>
								<h3 className='font-serif font-medium text-18  lg:text-24 text-center text-black'>
									We can craft a custom Kaer experience for you—anywhere in the
									world
								</h3>
								<Button
									small
									label='Start Planning'
									className='mt-20'
									variant='primary'
									onClick={() => {
										router.push('/experience/custom-experience')
									}} />
							</div>
						)}
					</div>
				)}
			{forHome && (
				<SlideshowNative
					disableObserver
					ref={slideshowRef}
					className='span-w-12-wider lg:span-w-7-wide gutter-gap-1 lg-max:scroll-pl-[var(--margin)] lg-max:margin-px-1 lg:margin-pr-1 lg:hidden'>
					{filteredGrid.map((card, index) => (
						<ExperienceGridCard key={index} {...card} onlyMobile />
					))}
				</SlideshowNative>
			)}
			<div
				className={clsx(
					'grid gap-20 auto-rows-auto grid-cols-[repeat(auto-fit,minmax(335px,1fr))] xl:grid-cols-4 ',
					forHome && 'lg-max:hidden',
				)}>
				{filteredGrid.map((item) => (
					<ExperienceGridCard key={item.slug} {...item} />
				))}
			</div>

			{forHome && (
				<Button
					className='lg:mt-50 mt-20 border border-black text-12 font-medium text-black'
					variant='secondary-transparent'
					href='/experiences'
					label='All Experiences' />
			)}
		</div>
	)
}
